import { HttpClient } from '@angular/common/http';
import { DOCUMENT, inject, Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Icons } from '../constants/icons.constants';

@Injectable()
export class IconService {
    private document: Document = inject(DOCUMENT);
    private sanitizer: DomSanitizer = inject(DomSanitizer);
    private httpClient: HttpClient = inject(HttpClient);

    public getIcon(
        url: string,
        preserveAspectRatio: Icons.PreserveAspectRatio = `xMidYMid`
    ): Observable<SVGElement> {
        return this.getSvgIconFromUrl(
            this.sanitizer.bypassSecurityTrustResourceUrl(
                url.startsWith('http')
                    ? url
                    : url.endsWith('.svg')
                      ? `assets/icons/${url}`
                      : `assets/icons/${url}.svg`
            ),
            preserveAspectRatio
        );
    }

    private getSvgIconFromUrl(
        safeUrl: SafeResourceUrl,
        preserveAspectRatio: Icons.PreserveAspectRatio
    ): Observable<SVGElement> {
        const url = this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, safeUrl);
        if (url == null) throw Error(`Cannot fetch icon.`);

        return this.fetchIcon(safeUrl).pipe(
            map((svg) =>
                cloneSvg(this.svgElementFromTrustedHTML(svg, preserveAspectRatio))
            )
        );
    }

    private svgElementFromTrustedHTML(
        trustedHTML: TrustedHTML,
        preserveAspectRatio: Icons.PreserveAspectRatio
    ): SVGElement {
        return this.setSvgAttributes(
            this.svgElementFromString(trustedHTML),
            preserveAspectRatio
        );
    }

    private svgElementFromString(str: TrustedHTML): SVGElement {
        const div = this.document.createElement('DIV');
        div.innerHTML = str as unknown as string;
        const svg = div.querySelector('svg') as SVGElement;

        if (!svg) throw Error('<svg> tag not found');

        return svg;
    }

    private setSvgAttributes(
        svg: SVGElement,
        preserveAspectRatio: Icons.PreserveAspectRatio
    ): SVGElement {
        svg.setAttribute('fit', '');
        svg.setAttribute('height', '100%');
        svg.setAttribute('width', '100%');
        svg.setAttribute('display', 'block');
        svg.setAttribute('preserveAspectRatio', `${preserveAspectRatio} meet`);
        svg.setAttribute('focusable', 'false'); // Disable IE11 default behavior to make SVGs focusable.

        return svg;
    }

    private fetchIcon(safeUrl: SafeResourceUrl): Observable<TrustedHTML> {
        const url: string =
            this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, safeUrl) || '';
        if (!url) throw Error(`Cannot fetch icon.`);

        return this.httpClient.get(url, { responseType: 'text' }).pipe(
            map((svg) => trustedHTMLFromString(svg)),
            catchError(() =>
                of(
                    // if passed a bad icon, show the circle-question mark icon
                    // added as a string just incase its removed or renamed
                    trustedHTMLFromString(`<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
  <path fill="currentColor" d="M12 24c6.628 0 12-5.372 12-12S18.628 0 12 0 0 5.372 0 12s5.372 12 12 12ZM7.96 7.748A2.63 2.63 0 0 1 10.433 6h2.733a2.958 2.958 0 0 1 2.958 2.958c0 1.06-.567 2.039-1.486 2.569l-1.514.867c-.01.61-.51 1.106-1.125 1.106a1.122 1.122 0 0 1-1.125-1.125v-.633c0-.403.216-.773.567-.975l2.077-1.19a.709.709 0 0 0-.352-1.322h-2.733a.37.37 0 0 0-.351.248l-.019.056c-.206.586-.853.891-1.434.685a1.129 1.129 0 0 1-.685-1.435l.02-.056-.006-.005ZM13.5 16.5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5.67-1.5 1.5-1.5 1.5.67 1.5 1.5Z"/>
</svg>`)
                )
            )
        );
    }
}

/** https://github.com/angular/components/blob/15.2.9/src/material/icon/trusted-types.ts */

let policy: TrustedTypePolicy | null | undefined;

declare interface TrustedHTML {
    __brand__: 'TrustedHTML';
}

declare interface TrustedTypePolicyFactory {
    createPolicy(
        policyName: string,
        policyOptions: {
            createHTML?: (input: string) => string;
        }
    ): TrustedTypePolicy;
}

declare interface TrustedTypePolicy {
    createHTML(input: string): TrustedHTML;
}

function getPolicy(): TrustedTypePolicy | null {
    if (policy === undefined) {
        policy = null;
        if (typeof window !== 'undefined') {
            const ttWindow = window as unknown as {
                trustedTypes?: TrustedTypePolicyFactory;
            };
            if (ttWindow.trustedTypes !== undefined) {
                policy = ttWindow.trustedTypes.createPolicy('angular#components', {
                    createHTML: (s: string) => s
                });
            }
        }
    }
    return policy;
}

function trustedHTMLFromString(html: string): TrustedHTML {
    return getPolicy()?.createHTML(html) || (html as unknown as TrustedHTML);
}

function cloneSvg(svg: SVGElement): SVGElement {
    return svg.cloneNode(true) as SVGElement;
}
